/**
 * Physical stock counts. MASTER_PROMPT section 6.23 and decision A20.
 *
 * The sequence is deliberate and the screen enforces it: open (which freezes
 * book stock at cutoff), count, sign off with a reason for every variance,
 * then post the adjustments.
 *
 * Two things the design will not compromise on, and the screen shows both:
 * a variance beyond the threshold demands a second blind count, and two blind
 * counts that disagree are recounted rather than averaged.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { ops } from "../../lib/api2";
import type {
  CountResult,
  DueCount,
  Location,
  ReasonCode,
} from "../../lib/types";
import { fmtDate, fmtINR, fmtQty, today } from "../../lib/format";

export function StockCounts() {
  const [due, setDue] = useState<DueCount[] | null>(null);
  const [reasons, setReasons] = useState<ReasonCode[]>([]);
  const [count, setCount] = useState<CountResult | null>(null);
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [recounts, setRecounts] = useState<Record<string, string>>({});
  const [chosen, setChosen] = useState<Record<string, number | "">>({});
  // From masters, so a new store is countable the day it is created.
  const [locations, setLocations] = useState<Location[]>([]);
  const [location, setLocation] = useState("");
  const [countType, setCountType] = useState("CYCLE");
  const [countDate, setCountDate] = useState(today);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);

  const loadDue = useCallback(() => {
    ops.dueCounts().then(setDue).catch(() => setDue([]));
  }, []);

  useEffect(() => {
    loadDue();
    api.reasonCodes().then(setReasons).catch(() => undefined);
    api
      .locations()
      .then((rows) => {
        setLocations(rows);
        setLocation((current) => current || rows[0]?.code || "");
      })
      .catch(() => undefined);
  }, [loadDue]);

  async function open() {
    setBusy(true);
    setBanner(null);
    try {
      const result = await ops.openCount({
        location_code: location,
        count_date: countDate,
        count_type: countType,
      });
      setCount(result);
      setCounted({});
      setRecounts({});
      setChosen({});
      setBanner({
        tone: "positive",
        text: `${result.count_no} opened. Book stock is frozen at ${fmtDate(countDate)} — anything posted now will not move the target.`,
      });
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  async function record() {
    if (!count) return;
    const quantities = Object.fromEntries(
      Object.entries(counted)
        .filter(([, v]) => v !== "")
        .map(([k, v]) => [k, Number(v)])
    );
    const second = Object.fromEntries(
      Object.entries(recounts)
        .filter(([, v]) => v !== "")
        .map(([k, v]) => [k, Number(v)])
    );
    if (!Object.keys(quantities).length) {
      setBanner({ tone: "warning", text: "Enter at least one counted figure." });
      return;
    }

    setBusy(true);
    try {
      const result = await ops.recordCount(
        count.count_id,
        quantities,
        Object.keys(second).length ? second : undefined
      );
      setCount(result);
      setBanner(
        result.warnings.length
          ? { tone: "warning", text: result.warnings.join(" ") }
          : { tone: "positive", text: "Counts recorded." }
      );
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  async function signOff() {
    if (!count) return;
    const reasonMap = Object.fromEntries(
      Object.entries(chosen)
        .filter(([, v]) => v !== "")
        .map(([k, v]) => [k, Number(v)])
    );
    setBusy(true);
    try {
      const result = await ops.signOffCount(count.count_id, reasonMap);
      setCount(result);
      setBanner({ tone: "positive", text: `${result.count_no} signed off.` });
    } catch (err) {
      const e = err as ApiError;
      setBanner({ tone: e.status === 422 ? "warning" : "negative", text: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function adjust() {
    if (!count) return;
    setBusy(true);
    try {
      const result = await ops.adjustCount(count.count_id);
      setCount(result);
      setBanner({ tone: "positive", text: result.warnings.join(" ") });
      loadDue();
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  const stage = count?.status ?? "NONE";
  const variances = count?.lines.filter((l) => Number(l.variance_qty ?? 0) !== 0) ?? [];

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Stock counts</h2>
          <p className="page__sub">
            Quarterly with head sign-off, plus monthly cycle counts on the
            high-value fast movers
          </p>
        </div>
      </header>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      {due && due.length > 0 && (
        <section className="card exceptions">
          <h3 className="section-title">Due now</h3>
          <ul className="exceptions__list">
            {due.map((d) => (
              <li key={`${d.location_code}-${d.count_type}`}>
                <span className="pill pill--warning">{d.count_type}</span>
                {d.location_name} —{" "}
                {d.last_counted
                  ? `last counted ${fmtDate(d.last_counted)}, ${d.days_since} days ago`
                  : "never counted"}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card">
        <h3 className="section-title">Open a count</h3>
        <p className="section-note">
          Book stock is frozen the moment the sheet is generated, so a movement
          posted while people are counting cannot move the target underneath
          them.
        </p>
        <div className="page__footer">
          <label className="inputgroup">
            <span className="label">Location</span>
            <select value={location} onChange={(e) => setLocation(e.target.value)}>
              {locations.map(({ code, name }) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="inputgroup">
            <span className="label">Type</span>
            <select
              value={countType}
              onChange={(e) => setCountType(e.target.value)}
            >
              <option value="CYCLE">Cycle — high-value fast movers</option>
              <option value="QUARTERLY">Quarterly — everything</option>
              <option value="AD_HOC">Ad hoc</option>
            </select>
          </label>
          <label className="inputgroup">
            <span className="label">Date</span>
            <input
              type="date"
              value={countDate}
              onChange={(e) => setCountDate(e.target.value)}
            />
          </label>
          <button className="btn btn--auto" onClick={open} disabled={busy}>
            Open count
          </button>
        </div>
      </section>

      {count && (
        <section className="card">
          <h3 className="section-title">
            {count.count_no}
            <span className="pill" style={{ marginLeft: "var(--sp-3)" }}>
              {stage.toLowerCase()}
            </span>
          </h3>
          <p className="section-note">
            {variances.length} variance(s) ·{" "}
            {fmtINR(count.total_variance_value)} at risk
          </p>

          <div className="tablewrap tablewrap--tall">
            <table className="table table--entry">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Book</th>
                  <th className="num">Counted</th>
                  <th className="num">Second count</th>
                  <th className="num">Variance</th>
                  <th className="num">Value</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {count.lines.map((line) => {
                  const variance = Number(line.variance_qty ?? 0);
                  return (
                    <tr
                      key={line.item_code}
                      className={line.needs_recount ? "row--warn" : ""}
                    >
                      <td>{line.item_name}</td>
                      <td className="num mono">{fmtQty(line.book_qty, 3)}</td>
                      <td>
                        <input
                          className="num mono"
                          inputMode="decimal"
                          value={counted[line.item_code] ?? ""}
                          placeholder="—"
                          disabled={stage !== "OPEN" && stage !== "COUNTED"}
                          onChange={(e) =>
                            setCounted((p) => ({
                              ...p,
                              [line.item_code]: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="num mono"
                          inputMode="decimal"
                          value={recounts[line.item_code] ?? ""}
                          placeholder={line.needs_recount ? "required" : "—"}
                          disabled={stage !== "OPEN" && stage !== "COUNTED"}
                          onChange={(e) =>
                            setRecounts((p) => ({
                              ...p,
                              [line.item_code]: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td
                        className={`num mono${variance !== 0 ? " cell--negative" : ""}`}
                      >
                        {line.variance_qty === null
                          ? "—"
                          : `${variance > 0 ? "▲ " : variance < 0 ? "▼ " : ""}${fmtQty(variance, 3)}`}
                      </td>
                      <td className="num mono">
                        {line.variance_value === null
                          ? "—"
                          : fmtINR(line.variance_value)}
                      </td>
                      <td>
                        {variance !== 0 ? (
                          <select
                            value={chosen[line.item_code] ?? ""}
                            disabled={stage !== "COUNTED"}
                            onChange={(e) =>
                              setChosen((p) => ({
                                ...p,
                                [line.item_code]:
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value),
                              }))
                            }
                          >
                            <option value="">Choose…</option>
                            {reasons.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.description}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="kpi__sub">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {count.warnings.map((w) => (
            <div key={w} className="banner banner--warning">
              {w}
            </div>
          ))}

          <div className="page__footer">
            <span className="entrytotals__sub">
              A variance cannot be adjusted without a reason code and a
              signature from someone other than the counter.
            </span>
            {(stage === "OPEN" || stage === "COUNTED") && (
              <button className="btn btn--ghost btn--auto" onClick={record} disabled={busy}>
                Record counts
              </button>
            )}
            {stage === "COUNTED" && (
              <button className="btn btn--auto" onClick={signOff} disabled={busy}>
                Sign off
              </button>
            )}
            {stage === "SIGNED" && (
              <button className="btn btn--auto" onClick={adjust} disabled={busy}>
                Post adjustments
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
